import { AddIcon } from "@chakra-ui/icons"
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Tab,
  Table,
  TableContainer,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react"
import { Api } from "../lib/api"
import React from "react"
import Link from "./Link"
import { FaDownload } from "react-icons/fa6"
import { addAlert, AlertType } from "@/lib/alerts"
import { Paginator } from "./Paginator"

const Materials = () => {
  const api = new Api()
  const [hasStatus, setHasStatus] = React.useState(false)
  const [materials, setMaterials] = React.useState([])
  const [overrides, setOverrides] = React.useState([])
  const [materialsOffset, setMaterialsOffset] = React.useState(0)
  const [overridesOffset, setOverridesOffset] = React.useState(0)
  const [materialsLimit] = React.useState(50)
  const [overridesLimit] = React.useState(50)
  const [materialsTotal, setMaterialsTotal] = React.useState(0)
  const [overridesTotal, setOverridesTotal] = React.useState(0)

  const getMaterials = async () => {
    try {
      const materialsList = await api.materialsList(
        materialsOffset,
        materialsLimit,
      )
      if (Array.isArray(materialsList)) {
        setMaterials(materialsList)
        console.log("Materials Successfully Fetched:", materialsList)
        setMaterialsTotal(materialsList.count)
      } else if (materialsList && materialsList.results) {
        console.log(
          "Materials Successfully Fetched elseif:",
          materialsList.results,
        )
        setMaterials(materialsList.results)
        setMaterialsTotal(materialsList.count || 0)
      } else {
        setMaterials([])
        setMaterialsTotal(0)
      }
    } catch (error) {
      addAlert(AlertType.ERROR, "Error fetching materials", error.message)
      setMaterials([])
      setMaterialsTotal(0)
    }
  }

  const getOverrides = async () => {
    try {
      const overridesList = await api.overridesList()
      if (Array.isArray(overridesList)) {
        setOverrides(overridesList)
        console.log("Overrides Successfully Fetched:", overridesList)
        setOverridesTotal(overridesList.length)
        console.log("Overrides Total:", overridesList.length)
      } else if (overridesList && overridesList.results) {
        console.log(
          "Overrides Successfully Fetched elseif:",
          overridesList.results,
        )
        setOverrides(overridesList.results)
        setOverridesTotal(overridesList.count || 0)
      } else {
        setOverrides([])
        setOverridesTotal(0)
      }
    } catch (error) {
      console.error("Error in getOverrides:", error)
      addAlert(AlertType.ERROR, "Error fetching overrides", error.error)
      setOverrides([])
      setOverridesTotal(0)
    }
  }

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const status = await api.statusRetrieve()
        setHasStatus(status)
        await getMaterials()
        await getOverrides()
      } catch (error) {
        console.error("Error in fetchData:", error.message)
        setHasStatus(false)
      }
    }

    fetchData()
  }, [materialsOffset, materialsLimit])

  const hasProxy = React.useMemo(() => {
    if (!hasStatus) return
    return hasStatus.behind_proxy
  }, [hasStatus])

  const handleFulfillMaterials = async () => {
    try {
      await api.materialsReceive()
      addAlert(
        AlertType.SUCCESS,
        "Materials",
        "Materials received successfully",
      )
      getMaterials()
    } catch (error) {
      console.error("Error in handleRecievingMaterials:", error)
      addAlert(AlertType.ERROR, "Error recieving materials", error.message)
    }
  }

  const materialList = () => {
    return (
      <TableContainer
        borderRadius="0.75em"
        overflowY="auto"
        height="100%"
        width="100%"
        bg="white"
      >
        <Table>
          <Thead>
            <Tr>
              <Th>Material ID</Th>
              <Th>Created</Th>
              <Th>Request</Th>
              <Th>Requestor</Th>
            </Tr>
          </Thead>
          <Tbody>
            {materials &&
              materials.map((material) => (
                <Tr key={material.id}>
                  <Td>
                    <div>
                      <span>{material.id}</span>
                    </div>
                  </Td>
                  <Td>
                    <div>
                      <span>{material.created}</span>
                    </div>
                  </Td>
                  <Td>
                    <div>
                      <span>{material.request}</span>
                    </div>
                  </Td>
                  <Td>
                    <div>
                      <span>{material.uploaded_by}</span>
                    </div>
                  </Td>
                </Tr>
              ))}
          </Tbody>
        </Table>
      </TableContainer>
    )
  }

  const overridesList = () => {
    return (
      <TableContainer
        borderRadius="0.75em"
        overflowY="auto"
        height="100%"
        width="100%"
        bg="white"
      >
        <Table>
          <Thead>
            <Tr>
              <Th>Overrides</Th>
            </Tr>
          </Thead>
          <Tbody>
            {overrides &&
              overrides.map((override) => (
                <Tr key={override}>
                  <Td>
                    <div>
                      <span>{override}</span>
                    </div>
                  </Td>
                </Tr>
              ))}
          </Tbody>
        </Table>
      </TableContainer>
    )
  }

  const hasProxyNoMaterials = () => {
    if (!materials || materials.length === 0) {
      return (
        <Alert status="info" position="relative" width="100%" height="auto">
          <HStack>
            <AlertIcon />
            <Heading size="sm" mt={0} mb={0}>
              You have no outstanding materials
            </Heading>
          </HStack>
        </Alert>
      )
    }
    return null
  }

  const noProxyHasMaterials = () => {
    if (!materials || materials.length === 0) {
      return (
        <Alert status="info" position="relative" width="100%" height="auto">
          <VStack
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={2}
          >
            <HStack>
              <AlertIcon />
              <Heading size="sm" mt={0} mb={0}>
                No Proxy Detected
              </Heading>
            </HStack>
            <Text whiteSpace="normal" overflowWrap="break-word">
              Download the Blind Proxy to manage materials.
            </Text>
            <Flex
              width="full"
              direction={{ base: "column", md: "row" }}
              justifyContent="right"
              gap={{ base: "1", md: "4" }}
              flexWrap="wrap"
            >
              <Link
                href="https://docs.blindinsight.io/download/"
                isExternal
                rel="noreferrer"
              >
                <Button
                  variant="primary"
                  title="Download the Blind Proxy"
                  size={{ base: "sm", md: "md" }}
                  rightIcon={<FaDownload />}
                >
                  Download the Blind Proxy
                </Button>
              </Link>
            </Flex>
          </VStack>
        </Alert>
      )
    }
    return null
  }

  const noProxyEmptyMaterials = () => {
    return (
      <>
        <Alert status="info" position="relative" width="100%" height="auto">
          <VStack
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={2}
          >
            <HStack>
              <AlertIcon />
              <Heading size="md" mt={0} mb={0}>
                No Proxy Detected
              </Heading>
            </HStack>
            <VStack>
              <Text
                variant="base"
                width="100%"
                whiteSpace="normal"
                overflowWrap="break-word"
              >
                Only encrypted data ever enters or exits Blind Insight, and we
                don&apos;t hold any keys. <br></br>Download the Blind Proxy to
                create accept key-sharing materials.
              </Text>
            </VStack>
            <Flex
              width="full"
              direction={{ base: "column", md: "row" }}
              justifyContent="right"
              gap={{ base: "1", md: "4" }}
              flexWrap="wrap"
            >
              <Link
                href="https://docs.blindinsight.io/download/"
                isExternal
                rel="noreferrer"
              >
                <Button
                  variant="primary"
                  title="Download the Blind Proxy"
                  size={{ base: "sm", md: "md" }}
                  rightIcon={<FaDownload />}
                >
                  Download the Blind Proxy
                </Button>
              </Link>
            </Flex>
          </VStack>
        </Alert>
      </>
    )
  }

  const noOverrides = () => {
    return (
      <>
        <Alert status="info" position="relative" width="100%" height="auto">
          <HStack>
            <AlertIcon />
            <Heading size="md" mt={0} mb={0}>
              No Overrides
            </Heading>
          </HStack>
        </Alert>
      </>
    )
  }

  const materialsPanel = () => {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        bg="Gray.100"
        p={4}
        borderRadius="0 0.75em 0.75em 0.75em"
        height={{ base: "50%", md: "100%" }}
      >
        <HStack
          justifyContent="space-between"
          alignItems={hasProxy ? "center" : "flex-start"}
        >
          {!hasProxy && materials && materials.length > 0 ? (
            noProxyHasMaterials()
          ) : (
            <span />
          )}
          {!hasProxy && (!materials || materials.length === 0)
            ? noProxyEmptyMaterials()
            : null}
          <Button
            aria-label={
              hasProxy
                ? "Receive all materials"
                : "You must be behind the proxy to receive materials"
            }
            disabled={!hasProxy || materialsTotal === 0}
            rightIcon={<AddIcon />}
            variant="primary"
            onClick={handleFulfillMaterials}
            flexShrink={0}
            minW="fit-content"
          >
            Receive Materials
          </Button>
        </HStack>
        <VStack height="calc(100% - 4em)" mt="1em">
          <Paginator
            offset={materialsOffset}
            total={materialsTotal}
            limit={materialsLimit}
            setOffset={setMaterialsOffset}
          />
          {materials.length > 0 ? materialList() : hasProxyNoMaterials()}
        </VStack>
      </Box>
    )
  }

  const overridesPanel = () => {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        bg="Gray.100"
        p={4}
        borderRadius="0 0.75em 0.75em 0.75em"
        height={{ base: "auto", md: "100%" }}
      >
        <Paginator
          offset={overridesOffset}
          total={overridesTotal}
          limit={overridesLimit}
          setOffset={setOverridesOffset}
        />
        {overrides.length > 0 ? overridesList() : noOverrides()}
      </Box>
    )
  }

  return (
    <Tabs variant="mini" display="flex" flexDirection="column" height="100%">
      <TabList>
        <Tab>Materials</Tab>
        <Tab>Overrides</Tab>
      </TabList>
      <TabPanels flexGrow={1}>
        <TabPanel height="100%" p={0}>
          {materialsPanel()}
        </TabPanel>
        <TabPanel height="100%" p={0}>
          {overridesPanel()}
        </TabPanel>
      </TabPanels>
    </Tabs>
  )
}

export default Materials
